import React from "react";

export interface CalculatorInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "prefix" | "size"
> {
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  wrapperClassName?: string;
  inputClassName?: string;
  prefixClassName?: string;
  suffixClassName?: string;
  contentAlign?: "start" | "center" | "end";
  error?: boolean;
  mobileFocusHighlight?: boolean;
  showFocusStyle?: boolean;
}

export const CalculatorInput = React.forwardRef<HTMLInputElement, CalculatorInputProps>(
  (
    {
      prefix,
      suffix,
      wrapperClassName = "",
      inputClassName = "",
      prefixClassName = "",
      suffixClassName = "",
      contentAlign = "start",
      error = false,
      mobileFocusHighlight = false,
      showFocusStyle = true,
      className = "",
      value,
      ...inputProps
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const effectiveFocused = isFocused && showFocusStyle;
    const hasAffix = Boolean(prefix || suffix);
    const usesInlineAffixLayout = contentAlign === "center" || contentAlign === "end";
    const displayValue = value === undefined || value === null ? "" : String(value);

    const getBorderColor = (hasError: boolean, focused: boolean): string => {
      if (hasError) return "#b40000";
      if (focused) return "#5164C8";
      return "#999999";
    };

    const getBackgroundClasses = (
      hasError: boolean,
      focused: boolean,
      mobileHighlight: boolean
    ): string => {
      if (hasError) return "bg-white";
      if (focused && mobileHighlight) return "bg-[#F0F8FF]";
      return "bg-white";
    };

    const inputBaseClasses = [
      "font-rubik min-w-0 appearance-none border-none bg-transparent text-[14px] leading-[1.25] font-medium text-[#1A1A1A] outline-none placeholder:font-normal placeholder:text-[#666666] md:text-[16px]",
      className,
      inputClassName,
    ]
      .filter(Boolean)
      .join(" ");

    const inlineAffixInputWidthCh = Math.max(displayValue.length, 1) + 0.5;

    const inputEventHandlers = {
      onFocus: (event: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(true);
        inputProps.onFocus?.(event);
      },
      onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(false);
        inputProps.onBlur?.(event);
      },
    };

    const renderPrefix = () =>
      prefix ? (
        <span
          className={[
            "flex shrink-0 items-center text-[16px] font-medium text-[#1A1A1A]",
            usesInlineAffixLayout ? "" : "pl-[8px]",
            prefixClassName,
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {prefix}
          {"\u2009"}
        </span>
      ) : null;

    const renderSuffix = () =>
      suffix ? (
        <div
          className={[
            "ml-[2px] flex h-full shrink-0 items-center justify-center text-center text-[#1A1A1A]",
            usesInlineAffixLayout ? "" : "pl-[4px]",
            suffixClassName,
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {"\u2009"}
          {suffix}
        </div>
      ) : null;

    const renderInput = (inputClasses: string, inputStyle?: React.CSSProperties) => (
      <input
        ref={ref}
        className={inputClasses}
        style={inputStyle}
        value={value}
        {...inputProps}
        {...inputEventHandlers}
      />
    );

    const renderInlineAffixContent = (
      outerJustify: "justify-center" | "justify-end",
      emptyTextAlign: "text-center" | "text-right"
    ) => (
      <div className={`flex h-full w-full min-w-0 items-center ${outerJustify}`}>
        <div
          className={[
            "inline-flex max-w-full min-w-0 items-center justify-center",
            hasAffix ? "" : "w-full",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {renderPrefix()}
          {renderInput(
            [
              inputBaseClasses,
              hasAffix
                ? "w-auto min-w-[2ch] shrink-0 !px-[0px] text-right"
                : `w-full ${emptyTextAlign}`,
            ].join(" "),
            hasAffix
              ? {
                  width: `${inlineAffixInputWidthCh}ch`,
                  fieldSizing: "content",
                }
              : undefined
          )}
          {renderSuffix()}
        </div>
      </div>
    );

    const renderCenteredContent = () => renderInlineAffixContent("justify-center", "text-center");

    const renderEndContent = () => renderInlineAffixContent("justify-end", "text-right");

    const renderStartContent = () => (
      <div className="flex h-full w-full min-w-0 items-center" style={{ minWidth: 0 }}>
        {renderPrefix()}
        {renderInput(
          [inputBaseClasses, "w-full text-right", prefix ? "pr-[8px]" : ""]
            .filter(Boolean)
            .join(" ")
        )}
        {renderSuffix()}
      </div>
    );

    const renderContent = () => {
      if (contentAlign === "center") return renderCenteredContent();
      if (contentAlign === "end") return renderEndContent();
      return renderStartContent();
    };

    return (
      <div
        className={[
          "bfl-calculator-input flex w-full shrink-0 rounded-[4px] border border-solid transition-colors duration-200",
          effectiveFocused ? "overflow-visible" : "overflow-hidden",
          getBackgroundClasses(error, effectiveFocused, mobileFocusHighlight),
          wrapperClassName,
        ]
          .filter(Boolean)
          .join(" ")}
        style={{ borderColor: getBorderColor(error, effectiveFocused) }}
        data-cmp="CalculatorInput"
        data-cmp-type="react"
        data-cmp-title={inputProps["aria-label"] || "Calculator Input"}
      >
        {renderContent()}
      </div>
    );
  }
);

CalculatorInput.displayName = "CalculatorInput";
